import { useState } from "react";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { ColorSwatch } from "@/components/ColorPicker";
import { Users, Send, Trash2, Eye, Pencil, ArrowDownLeft, ArrowUpRight, Mail } from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";

export function SharingSettings() {
  const { outgoingShares, incomingShares, sharesApi } = useApp();
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [permission, setPermission] = useState("view");
  const [color, setColor] = useState("#8B5CF6");
  const [busy, setBusy] = useState(false);

  const share = async () => {
    const e = email.trim().toLowerCase();
    if (!e || !e.includes("@")) { toast.error("Enter a valid email"); return; }
    if (e === user?.email?.toLowerCase()) { toast.error("You can't share with yourself"); return; }
    setBusy(true);
    try { await sharesApi.create(e, permission, color); toast.success(`Calendar shared with ${e}`); setEmail(""); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not share"); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-3xl p-5 mb-4" style={{ background: "var(--bg-card)", border: "1px solid var(--border-subtle)" }} data-testid="sharing-settings">
      <div className="flex items-center gap-2 mb-1">
        <Users size={18} style={{ color: "var(--accent-primary)" }} />
        <h3 className="font-display font-bold text-lg" style={{ color: "var(--text-primary)" }}>Calendar Sharing</h3>
      </div>
      <p className="text-xs mb-4" style={{ color: "var(--text-muted)" }}>Share your calendar with a friend by email. If you both share with each other, you'll each see the other's events overlaid — like one shared calendar.</p>

      {/* Share form */}
      <div className="p-3 rounded-2xl mb-4" style={{ background: "var(--bg-timeline-track)", border: "1px solid var(--border-subtle)" }}>
        <label className="text-[11px] uppercase tracking-wider mb-2 flex items-center gap-1" style={{ color: "var(--text-muted)" }}><Mail size={12} /> Friend's email</label>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="friend@example.com" type="email"
            className="flex-1 h-10 rounded-xl" style={{ background: "var(--bg-card)", borderColor: "var(--border-subtle)", color: "var(--text-primary)" }} data-testid="share-email-input" />
          <div className="flex gap-2">
            <div className="flex p-1 rounded-xl" style={{ background: "var(--bg-card)" }}>
              {[{ v: "view", l: "View", i: Eye }, { v: "edit", l: "Edit", i: Pencil }].map((p) => (
                <button key={p.v} onClick={() => setPermission(p.v)} className="px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 transition-colors"
                  style={{ background: permission === p.v ? "var(--accent-primary)" : "transparent", color: permission === p.v ? "#fff" : "var(--text-secondary)" }} data-testid={`share-perm-${p.v}`}>
                  <p.i size={13} /> {p.l}
                </button>
              ))}
            </div>
            <div className="w-11"><ColorSwatch color={color} onChange={setColor} testId="share-color-swatch" /></div>
          </div>
        </div>
        <Button onClick={share} disabled={busy} className="w-full mt-3 h-10 text-white font-semibold" style={{ background: "var(--accent-primary)" }} data-testid="share-submit">
          <Send size={15} /> {busy ? "Sharing..." : "Share My Calendar"}
        </Button>
      </div>

      {/* Incoming (their calendars) */}
      <div className="mb-4">
        <p className="text-[11px] uppercase tracking-wider mb-2 flex items-center gap-1" style={{ color: "var(--text-muted)" }}><ArrowDownLeft size={12} /> Shared with me ({incomingShares.length})</p>
        <div className="space-y-2">
          <AnimatePresence>
            {incomingShares.map((s) => (
              <motion.div key={s.id} layout initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }}
                className="flex items-center gap-3 p-3 rounded-2xl" style={{ background: "var(--bg-timeline-track)", border: "1px solid var(--border-subtle)", borderLeft: `3px solid ${s.color}` }} data-testid="incoming-share">
                <div className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0" style={{ background: s.color }}>{s.owner_name?.[0] || "?"}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate" style={{ color: "var(--text-primary)" }}>{s.owner_name}</p>
                  <p className="text-[11px] truncate" style={{ color: "var(--text-muted)" }}>{s.owner_email} · {s.permission === "edit" ? "can edit" : "view only"}</p>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px]" style={{ color: "var(--text-muted)" }}>Show</span>
                  <Switch checked={s.visible} onCheckedChange={(v) => sharesApi.update(s.id, { visible: v })} data-testid="incoming-visible-toggle" />
                </div>
                <button onClick={() => sharesApi.remove(s.id)} className="p-1.5 rounded-lg text-red-400 hover:bg-red-500/10"><Trash2 size={15} /></button>
              </motion.div>
            ))}
          </AnimatePresence>
          {incomingShares.length === 0 && <p className="text-xs py-2" style={{ color: "var(--text-muted)" }}>No one has shared their calendar with you yet.</p>}
        </div>
      </div>

      {/* Outgoing (I shared) */}
      <div>
        <p className="text-[11px] uppercase tracking-wider mb-2 flex items-center gap-1" style={{ color: "var(--text-muted)" }}><ArrowUpRight size={12} /> I've shared with ({outgoingShares.length})</p>
        <div className="space-y-2">
          <AnimatePresence>
            {outgoingShares.map((s) => (
              <motion.div key={s.id} layout initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }}
                className="flex items-center gap-3 p-3 rounded-2xl" style={{ background: "var(--bg-timeline-track)", border: "1px solid var(--border-subtle)", borderLeft: `3px solid ${s.color}` }} data-testid="outgoing-share">
                <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ background: `color-mix(in srgb, ${s.color} 25%, transparent)`, color: s.color }}><Mail size={16} /></div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate" style={{ color: "var(--text-primary)" }}>{s.recipient_email}</p>
                  <p className="text-[11px]" style={{ color: "var(--text-muted)" }}>{s.recipient_id ? "Active" : "Pending — waiting for them to sign in"} · {s.permission === "edit" ? "can edit" : "view only"}</p>
                </div>
                <button onClick={() => sharesApi.remove(s.id)} className="p-1.5 rounded-lg text-red-400 hover:bg-red-500/10" data-testid="revoke-share"><Trash2 size={15} /></button>
              </motion.div>
            ))}
          </AnimatePresence>
          {outgoingShares.length === 0 && <p className="text-xs py-2" style={{ color: "var(--text-muted)" }}>You haven't shared your calendar yet.</p>}
        </div>
      </div>
    </div>
  );
}
